import { useCallback } from 'react';
import type { ContextMenuItem } from '../../components/ContextMenu';
import { getAddPickerEntries } from '../entities/registry';
import { useCanvasStores } from '../stores/CanvasContext';
import { useCanvasActions } from './useCanvasActions';
import type { ShapeData } from '../entityTypes';

const pickerEntries = getAddPickerEntries();

/**
 * Returns a function that builds "Hinzufügen" submenu items from the entity registry.
 * Instant types create the entity directly; taskPicker types open the task picker modal.
 * Entries with children render a nested submenu for variant selection (e.g. shape types).
 */
export function useAddSubmenuItems() {
  const { uiStore } = useCanvasStores();
  const { handleAddByKind, handleAddShape } = useCanvasActions();

  const buildAddSubmenuItems = useCallback((layerId?: string, screenPos?: { x: number; y: number }): ContextMenuItem[] => {
    return pickerEntries.map(({ kind, entry }) => {
      // Entries with children: render as submenu with variant selection
      if (entry.children && entry.children.length > 0) {
        return {
          id: `add-${kind}`,
          label: entry.label,
          icon: entry.icon,
          iconColor: entry.iconColor,
          children: entry.children.map(child => ({
            id: `add-${kind}-${child.id}`,
            label: child.label,
            icon: child.icon,
            onClick: () => {
              if (kind === 'shape') {
                handleAddShape(layerId, screenPos, child.id as ShapeData['shapeType']);
              } else {
                handleAddByKind(kind, layerId, undefined, screenPos);
              }
            },
          })),
        };
      }

      return {
        id: `add-${kind}`,
        label: entry.label,
        icon: entry.icon,
        iconColor: entry.iconColor,
        onClick: () => {
          if (entry.mode === 'taskPicker') {
            uiStore.send({ type: 'addPicker.set', state: { layerId, screenPos } });
          } else {
            handleAddByKind(kind, layerId, undefined, screenPos);
          }
        },
      };
    });
  }, [uiStore, handleAddByKind, handleAddShape]);

  return { buildAddSubmenuItems };
}
